import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import AppContext from '../contexts/AppContext';
import { hasPermission } from '../utils/permissionUtils';
import { ROLE_HIERARCHY } from '../utils/roleUtils';

const NAVIGATION_ITEMS = [
  {
    id: 'home',
    path: '/home',
    icon: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
      />
    ),
    showForRoles: ['user', 'admin', 'superadmin', 'manager'],
    showCondition: () => true
  },
  {
    id: 'request',
    path: '/request',
    icon: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 4v16m8-8H4"
      />
    ),
    showCondition: (serviceDeskConfig, currentUser) => {
      if (hasPermission(['technician'], currentUser?.role)) {
        return currentUser?.allowed_ticket_creation_desks?.length > 0;
      }
      return true;
    },
    showForRoles: ['user', 'admin', 'superadmin', 'manager', 'technician'],
  },
  {
    id: 'intervention-points',
    path: '/intervention-points',
    icon: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
      />
    ),
    showForRoles: ['user', 'admin', 'superadmin', 'manager'],
    showCondition: (serviceDeskConfig) => serviceDeskConfig?.hasInterventionPointTab
  },
  {
    id: 'issues',
    path: '/issues',
    icon: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 7h18M3 12h18M3 17h18"
      />
    ),
    showForRoles: [...ROLE_HIERARCHY],
    showCondition: () => true
  }
];

const BottomBar = ({ userRole = 'user', serviceDesk, currentUser }) => {
  const visibleItems = useMemo(() => {
    return NAVIGATION_ITEMS.filter(({ showForRoles, showCondition }) => {
      return showForRoles.includes(userRole) &&
        showCondition(serviceDesk, currentUser);
    });
  }, [userRole, serviceDesk, currentUser]);

  return (
    <div className="fixed h-[75px] bg-white inset-x-0 bottom-0 z-100 border-t border-gray-200" style={{ zIndex: 150 }}>
      <div className={`Wrapper w-full max-w-screen-md mx-auto h-[75px] flex items-center ${
        visibleItems.length === 1 ? 'justify-center' : 'justify-around'
      }`}>
        {visibleItems.map((item) => <NavItem key={item.id} item={item} />)}
      </div>
    </div>
  );
};

const NavItem = ({ item }) => {
  const { id, path, icon } = item;
  const { t } = useTranslation();
  const location = useLocation();
  const active = location.pathname.startsWith(path);

  // Literal-keyed lookup (one literal translation key per nav item) keeps the
  // i18n Jira-integrity static guard satisfied — no dynamic value is translated.
  const NAV_LABELS = {
    home: t('Home'),
    request: t('Request'),
    'intervention-points': t('Stores'),
    issues: t('Tickets'),
  };
  const label = NAV_LABELS[id];

  return (
    <Link
      key={id}
      to={path}
      className={`flex flex-col items-center justify-center py-3 px-2 transition-colors ${
        active
          ? 'text-black font-bold border-t border-black border-t-2'
          : 'text-gray-600 hover:text-gray-900'
      }`}
    >
      <div className="pb-1 rounded-full">
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          {icon}
        </svg>
      </div>
      <span className="text-[12px] font-medium">{label}</span>
    </Link>
  );
};

BottomBar.propTypes = {
  serviceDesk: PropTypes.object,
  userRole: PropTypes.oneOf(['user', 'admin', 'manager', 'technician']),
  currentUser: PropTypes.object
};

export { BottomBar };
